import { useEffect, useState } from "react";
import mermaid from "mermaid";
import { ArrowDown, CircleCheckBig } from "lucide-react";
import * as htmlToImage from "html-to-image";

const MermaidRender = ({
  chart,
  diagramId,
}: {
  chart: string;
  diagramId: string;
}) => {
  const [diagram, setDiagram] = useState<string | boolean>(true);
  const [downloadedSvg, setDownloadedSvg] = useState(false);
  const [downloadedPng, setDownloadedPng] = useState(false);
  const safeDiagramId = `id-${diagramId}`;

  // Reset download states after 3 seconds
  useEffect(() => {
    if (downloadedSvg) {
      const timer = setTimeout(() => {
        setDownloadedSvg(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadedSvg]);

  useEffect(() => {
    if (downloadedPng) {
      const timer = setTimeout(() => {
        setDownloadedPng(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadedPng]);

  useEffect(() => {
    const render = async () => {
      try {
        if (await mermaid.parse(chart, { suppressErrors: true })) {
          const { svg } = await mermaid.render(safeDiagramId, chart);
          setDiagram(svg);
        } else {
          setDiagram(false);
        }
      } catch (error) {
        console.error("Something happened during rendering diagram: ", error);
        const elementsToRemove = document.querySelectorAll('[id^="dmermaid-"]');
        elementsToRemove.forEach((element) => {
          element.parentNode?.removeChild(element);
        });
        console.error(error);
        setDiagram(false);
      }
    };
    render();
  }, [chart, safeDiagramId]);

  const exportAsSvg = () => {
    const svgElement = document.getElementById(safeDiagramId);
    if (svgElement) {
      const svgContent = svgElement.outerHTML;
      const base64Data = btoa(svgContent);
      const downloadLink = document.createElement("a");
      const fileName = `diagram_${new Date()
        .toISOString()
        .slice(0, -5)
        .replace(/[-:]/g, "")}.svg`;
      downloadLink.download = fileName;
      downloadLink.href = "data:image/svg+xml;base64," + base64Data;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      setDownloadedSvg(true);
    }
  };

  const exportAsPng = async () => {
    const element = document.getElementById(safeDiagramId);
    if (!element) return;

    try {
      const surfaceOverlay = getComputedStyle(document.documentElement)
        .getPropertyValue("--color-surface-overlay")
        .trim() || "#ffffff";

      const dataUrl = await htmlToImage.toPng(element, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: surfaceOverlay,
      });

      const link = document.createElement("a");
      link.download = `diagram_${new Date()
        .toISOString()
        .slice(0, -5)
        .replace(/[-:]/g, "")}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadedPng(true);
    } catch (error) {
      console.error("Error exporting PNG:", error);
    }
  };

  if (diagram === true) {
    return <p className="text-gray-500">Rendering diagram...</p>;
  } else if (diagram === false) {
    return (
      <p className="text-red-500">
        Unsupported diagram format. Please try a different diagram...
      </p>
    );
  }

  return (
    <div className="relative w-full">
      <div className="absolute right-0 top-0 z-10 flex gap-2">
        {/* SVG Download Button */}
        <button
          onClick={exportAsSvg}
          className="flex gap-1 items-center bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-3 rounded text-xs"
        >
          {downloadedSvg ? (
            <>
              <CircleCheckBig size={12} />
              <span>SVG</span>
            </>
          ) : (
            <>
              <ArrowDown size={12} />
              <span>SVG</span>
            </>
          )}
        </button>

        {/* PNG Download Button */}
        <button
          onClick={exportAsPng}
          className="flex gap-1 items-center bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-1 px-3 rounded text-xs"
        >
          {downloadedPng ? (
            <>
              <CircleCheckBig size={12} />
              <span>PNG</span>
            </>
          ) : (
            <>
              <ArrowDown size={12} />
              <span>PNG</span>
            </>
          )}
        </button>
      </div>

      <div
        id={safeDiagramId}
        className="w-full overflow-auto bg-surface-overlay p-4 mt-8"
        dangerouslySetInnerHTML={{ __html: diagram as string }}
      />
    </div>
  );
};

export default MermaidRender;
